import { __extends } from "tslib";
import { Matrix, Vector3, Vector2 } from "../../Maths/math.vector";
import { Texture } from "../../Materials/Textures/texture";
import { RenderTargetTexture } from "../../Materials/Textures/renderTargetTexture";
import { BlurPostProcess } from "../../PostProcesses/blurPostProcess";
import { Plane } from '../../Maths/math.plane';
/**
 * Mirror texture can be used to simulate the view from a mirror in a scene.
 * It will dynamically be rendered every frame to adapt to the camera point of view.
 * You can then easily use it as a reflectionTexture on a flat surface.
 * In case the surface is not a plane, please consider relying on reflection probes.
 * @see https://doc.babylonjs.com/how_to/reflect#mirrors
 */
var MirrorTexture = /** @class */ (function (_super) {
    __extends(MirrorTexture, _super);
    /**
     * Instantiates a Mirror Texture.
     * Mirror texture can be used to simulate the view from a mirror in a scene.
     * It will dynamically be rendered every frame to adapt to the camera point of view.
     * You can then easily use it as a reflectionTexture on a flat surface.
     * In case the surface is not a plane, please consider relying on reflection probes.
     * @see https://doc.babylonjs.com/how_to/reflect#mirrors
     * @param name
     * @param size
     * @param scene
     * @param generateMipMaps
     * @param type
     * @param samplingMode
     * @param generateDepthBuffer
     */
    function MirrorTexture(name, size, scene, generateMipMaps, type, samplingMode, generateDepthBuffer) {
        if (type === void 0) { type = 0; }
        if (samplingMode === void 0) { samplingMode = Texture.BILINEAR_SAMPLINGMODE; }
        if (generateDepthBuffer === void 0) { generateDepthBuffer = true; }
        var _this = _super.call(this, name, size, scene, generateMipMaps, true, type, false, samplingMode, generateDepthBuffer) || this;
        _this.scene = scene;
        /**
         * Define the reflection plane we want to use. The mirrorPlane is usually set to the constructed reflector.
         * It is possible to directly set the mirrorPlane by directly using a Plane(a, b, c, d) where a, b and c give the plane normal vector (a, b, c) and d is a scalar displacement from the mirrorPlane to the origin. However in all but the very simplest of situations it is more straight forward to set it to the reflector as stated in the doc.
         * @see https://doc.babylonjs.com/how_to/reflect#mirrors
         */
        _this.mirrorPlane = new Plane(0, 1, 0, 1);
        _this._transformMatrix = Matrix.Zero();
        _this._mirrorMatrix = Matrix.Zero();
        _this._adaptiveBlurKernel = 0;
        _this._blurKernelX = 0;
        _this._blurKernelY = 0;
        _this._blurRatio = 1.0;
        _this.ignoreCameraViewport = true;
        _this._updateGammaSpace();
        _this._imageProcessingConfigChangeObserver = scene.imageProcessingConfiguration.onUpdateParameters.add(function () {
            _this._updateGammaSpace;
        });
        _this.onBeforeRenderObservable.add(function () {
            Matrix.ReflectionToRef(_this.mirrorPlane, _this._mirrorMatrix);
            _this._savedViewMatrix = scene.getViewMatrix();
            _this._mirrorMatrix.multiplyToRef(_this._savedViewMatrix, _this._transformMatrix);
            scene.setTransformMatrix(_this._transformMatrix, scene.getProjectionMatrix());
            scene.clipPlane = _this.mirrorPlane;
            scene.getEngine().cullBackFaces = false;
            scene._mirroredCameraPosition = Vector3.TransformCoordinates(scene.activeCamera.globalPosition, _this._mirrorMatrix);
        });
        _this.onAfterRenderObservable.add(function () {
            scene.setTransformMatrix(_this._savedViewMatrix, scene.getProjectionMatrix());
            scene.getEngine().cullBackFaces = true;
            scene._mirroredCameraPosition = null;
            scene.clipPlane = null;
        });
        return _this;
    }
    Object.defineProperty(MirrorTexture.prototype, "blurRatio", {
        get: function () {
            return this._blurRatio;
        },
        /**
         * Define the blur ratio used to blur the reflection if needed.
         */
        set: function (value) {
            if (this._blurRatio === value) {
                return;
            }
            this._blurRatio = value;
            this._preparePostProcesses();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MirrorTexture.prototype, "adaptiveBlurKernel", {
        /**
         * Define the adaptive blur kernel used to blur the reflection if needed.
         * This will autocompute the closest best match for the `blurKernel`
         */
        set: function (value) {
            this._adaptiveBlurKernel = value;
            this._autoComputeBlurKernel();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MirrorTexture.prototype, "blurKernel", {
        /**
         * Define the blur kernel used to blur the reflection if needed.
         * Please consider using `adaptiveBlurKernel` as it could find the closest best value for you.
         */
        set: function (value) {
            this.blurKernelX = value;
            this.blurKernelY = value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MirrorTexture.prototype, "blurKernelX", {
        get: function () {
            return this._blurKernelX;
        },
        /**
         * Define the blur kernel on the X Axis used to blur the reflection if needed.
         * Please consider using `adaptiveBlurKernel` as it could find the closest best value for you.
         */
        set: function (value) {
            if (this._blurKernelX === value) {
                return;
            }
            this._blurKernelX = value;
            this._preparePostProcesses();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(MirrorTexture.prototype, "blurKernelY", {
        get: function () {
            return this._blurKernelY;
        },
        /**
         * Define the blur kernel on the Y Axis used to blur the reflection if needed.
         * Please consider using `adaptiveBlurKernel` as it could find the closest best value for you.
         */
        set: function (value) {
            if (this._blurKernelY === value) {
                return;
            }
            this._blurKernelY = value;
            this._preparePostProcesses();
        },
        enumerable: false,
        configurable: true
    });
    MirrorTexture.prototype._autoComputeBlurKernel = function () {
        var engine = this.getScene().getEngine();
        var dw = this.getRenderWidth() / engine.getRenderWidth();
        var dh = this.getRenderHeight() / engine.getRenderHeight();
        this.blurKernelX = this._adaptiveBlurKernel * dw;
        this.blurKernelY = this._adaptiveBlurKernel * dh;
    };
    MirrorTexture.prototype._onRatioRescale = function () {
        if (this._sizeRatio) {
            this.resize(this._initialSizeParameter);
            if (!this._adaptiveBlurKernel) {
                this._preparePostProcesses();
            }
        }
        if (this._adaptiveBlurKernel) {
            this._autoComputeBlurKernel();
        }
    };
    MirrorTexture.prototype._updateGammaSpace = function () {
        this.gammaSpace = !this.scene.imageProcessingConfiguration.isEnabled || !this.scene.imageProcessingConfiguration.applyByPostProcess;
    };
    MirrorTexture.prototype._preparePostProcesses = function () {
        this.clearPostProcesses(true);
        if (this._blurKernelX && this._blurKernelY) {
            var engine = this.getScene().getEngine();
            var textureType = engine.getCaps().textureFloatRender ? 1 : 2;
            this._blurX = new BlurPostProcess("horizontal blur", new Vector2(1.0, 0), this._blurKernelX, this._blurRatio, null, Texture.BILINEAR_SAMPLINGMODE, engine, false, textureType);
            this._blurX.autoClear = false;
            if (this._blurRatio === 1 && this.samples < 2 && this._texture) {
                this._blurX.inputTexture = this._texture;
            }
            else {
                this._blurX.alwaysForcePOT = true;
            }
            this._blurY = new BlurPostProcess("vertical blur", new Vector2(0, 1.0), this._blurKernelY, this._blurRatio, null, Texture.BILINEAR_SAMPLINGMODE, engine, false, textureType);
            this._blurY.autoClear = false;
            this._blurY.alwaysForcePOT = this._blurRatio !== 1;
            this.addPostProcess(this._blurX);
            this.addPostProcess(this._blurY);
        }
        else {
            if (this._blurY) {
                this.removePostProcess(this._blurY);
                this._blurY.dispose();
                this._blurY = null;
            }
            if (this._blurX) {
                this.removePostProcess(this._blurX);
                this._blurX.dispose();
                this._blurX = null;
            }
        }
    };
    /**
     * Clone the mirror texture.
     * @returns the cloned texture
     */
    MirrorTexture.prototype.clone = function () {
        var scene = this.getScene();
        if (!scene) {
            return this;
        }
        var textureSize = this.getSize();
        var newTexture = new MirrorTexture(this.name, textureSize.width, scene, this._renderTargetOptions.generateMipMaps, this._renderTargetOptions.type, this._renderTargetOptions.samplingMode, this._renderTargetOptions.generateDepthBuffer);
        // Base texture
        newTexture.hasAlpha = this.hasAlpha;
        newTexture.level = this.level;
        // Mirror Texture
        newTexture.mirrorPlane = this.mirrorPlane.clone();
        if (this.renderList) {
            newTexture.renderList = this.renderList.slice(0);
        }
        return newTexture;
    };
    /**
     * Serialize the texture to a JSON representation you could use in Parse later on
     * @returns the serialized JSON representation
     */
    MirrorTexture.prototype.serialize = function () {
        if (!this.name) {
            return null;
        }
        var serializationObject = _super.prototype.serialize.call(this);
        serializationObject.mirrorPlane = this.mirrorPlane.asArray();
        return serializationObject;
    };
    /**
     * Dispose the texture and release its associated resources.
     */
    MirrorTexture.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this.scene.imageProcessingConfiguration.onUpdateParameters.remove(this._imageProcessingConfigChangeObserver);
    };
    return MirrorTexture;
}(RenderTargetTexture));
export { MirrorTexture };
Texture._CreateMirror = function (name, renderTargetSize, scene, generateMipMaps) {
    return new MirrorTexture(name, renderTargetSize, scene, generateMipMaps);
};
//# sourceMappingURL=mirrorTexture.js.map